import React from 'react';
import { theme } from '../styles/theme';
import { Footer } from '../components/Footer';
import { BookIcon, EditIcon, UsersIcon, TwitterIcon, BlueskyIcon } from '../components/Icons';
import { WaitlistModal } from '../components/WaitlistModal';

interface BlogPostData {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  author: string;
  category: string;
  readTime: string;
  icon: React.ReactNode;
  shareText: string;
  hashtags: string[];
  imageUrl?: string;
  briefTemplate?: string;
}

export const BlogPostPage = () => {
  // Get post slug from URL
  const slug = window.location.pathname.replace(/\/+$/, '').split('/').pop();
  const canonicalUrl = `https://manuscriptmatch.com/blog/${slug}`;
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  // Helper function to generate Twitter share URL
  const getTwitterShareUrl = (post: BlogPostData) => {
    const url = canonicalUrl;
    const text = post.shareText;
    const hashtags = post.hashtags.join(',');
    return `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}&hashtags=${hashtags}`;
  };

  // Helper function to generate Bluesky share URL
  const getBlueskyShareUrl = (post: BlogPostData) => {
    const url = canonicalUrl;
    const hashtagsText = post.hashtags.map(tag => `#${tag}`).join(' ');
    const text = `${post.shareText}\n\n${hashtagsText}`;
    return `https://bsky.app/intent/compose?text=${encodeURIComponent(text + '\n\n' + url)}`;
  };

  // Helper function to update meta tags for SEO
  const updateMetaTags = (post: BlogPostData) => {
    // Keep the complete build-time metadata when this article was prerendered.
    if (document.getElementById('marketing-structured-data') &&
        document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.href === canonicalUrl) return;

    // Update page title
    document.title = `${post.title} | Manuscript Match Blog`;

    // Update or create meta description
    const updateOrCreateMeta = (name: string, content: string, property?: boolean) => {
      const attribute = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Standard meta tags
    updateOrCreateMeta('description', post.excerpt);
    updateOrCreateMeta('author', post.author);
    updateOrCreateMeta('keywords', `beta readers, manuscript feedback, writing tips, literary agents, ${post.category.toLowerCase()}`);

    // Open Graph tags
    updateOrCreateMeta('og:title', post.title, true);
    updateOrCreateMeta('og:description', post.excerpt, true);
    updateOrCreateMeta('og:type', 'article', true);
    updateOrCreateMeta('og:url', canonicalUrl, true);
    updateOrCreateMeta('og:site_name', 'Manuscript Match', true);
    updateOrCreateMeta('article:published_time', new Date(post.date).toISOString(), true);
    updateOrCreateMeta('article:author', post.author, true);
    updateOrCreateMeta('article:section', post.category, true);

    // Twitter Card tags
    updateOrCreateMeta('twitter:card', 'summary_large_image');
    updateOrCreateMeta('twitter:title', post.title);
    updateOrCreateMeta('twitter:description', post.excerpt);
    const imageUrl = new URL(post.imageUrl || '/social-preview.png', 'https://manuscriptmatch.com').href;
    updateOrCreateMeta('twitter:image', imageUrl);
    updateOrCreateMeta('og:image', imageUrl, true);
    updateOrCreateMeta('twitter:image:alt', post.imageUrl ? post.title : 'Manuscript Match: the right readers for your next draft. In development; join the waitlist.');
    updateOrCreateMeta('og:image:alt', post.imageUrl ? post.title : 'Manuscript Match: the right readers for your next draft. In development; join the waitlist.', true);

    // Add JSON-LD structured data
    let structuredData = document.querySelector('#blog-post-structured-data') as HTMLScriptElement;
    if (!structuredData) {
      structuredData = document.createElement('script');
      structuredData.id = 'blog-post-structured-data';
      structuredData.type = 'application/ld+json';
      document.head.appendChild(structuredData);
    }
    structuredData.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      'headline': post.title,
      'description': post.excerpt,
      'author': {
        '@type': 'Organization',
        'name': post.author
      },
      'publisher': {
        '@type': 'Organization',
        'name': 'Manuscript Match',
        'logo': {
          '@type': 'ImageObject',
          'url': 'https://manuscriptmatch.com/ManuscriptMatchLogo.png'
        }
      },
      'datePublished': new Date(post.date).toISOString(),
      'mainEntityOfPage': {
        '@type': 'WebPage',
        '@id': canonicalUrl
      },
      'articleSection': post.category,
      'keywords': `beta readers, manuscript feedback, writing tips, literary agents, ${post.category.toLowerCase()}`
    });

    // Add canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;
  };

  // Blog posts data (same as BlogPage but with full content)
  const blogPosts: BlogPostData[] = [
    {
      id: '7',
      slug: 'beta-reader-brief-template',
      title: "Beta Reader Brief Template: What to Send With Your Manuscript",
      excerpt: "Prepare your manuscript for beta readers with a clear brief, a practical checklist and five feedback questions. Includes a free editable template.",
      content: `
A beta reader brief is a short note you send with your manuscript. It gives your reader the essentials: what kind of book it is, how much they'll be reading, which draft they're getting, what feedback would help and when you'd like it back.

Agree on the reading and deadline before sending the file. Include the brief with your manuscript so your reader has everything in one place. You can copy the template below into an email or a separate document.

## Start with the book and the amount of reading

Give the title, genre, intended readership and word count of the file you are sending. Distinguish a complete novel from an opening sample. “Adult fantasy, 84,000 words, complete draft” sets a different expectation from “the first three chapters, 9,500 words”.

Add a sentence or two about the premise, then let the pages speak for themselves. You'll learn more from the reader's own impressions than from explaining what you hope they'll notice.

## Name the draft and keep the file stable

Use a recognisable filename such as Glass-Harbour-v3-2026-10-30.docx, and repeat the version in your brief. Keep a copy of exactly what you sent. Chapter numbers or scene references will make feedback easier to locate later.

If you revise while someone is reading, avoid silently replacing their file. Either save the changes for the next round or ask whether they would prefer to switch versions. A reader's confusion may belong to the older draft; you need to know which one they saw.

## Explain what feedback would help

Say what matters for this draft: perhaps the opening's clarity, a relationship or the pace of the middle. A short overall response and a few questions can give readers a useful structure. Let them know whether you'd also welcome notes in the margins.

Reedsy's <a href="https://reedsy.com/blog/beta-readers/">guide to working with beta readers</a> distinguishes the reader's experience from a professional edit. Use your brief to make that distinction practical: “Please tell me where you lost the thread” gives someone a clearer task than “Please edit my book”.

Confirm whether this is a volunteer read, a critique swap or a paid service, and agree on how much feedback each person can offer. For a swap, discuss both manuscripts and both deadlines.

## Include content notes and a workable deadline

Give readers enough information to decide whether the material is right for them before they open it. Be specific where useful: “on-page violence” is more informative than “dark themes”. Offer more detail privately if they want it, and let them decline or stop without asking them to explain personal experiences.

Ask which file format they can comfortably read, check that the link opens with the intended access, and agree on whether comments should go in the file or a separate response. Ask them to keep the draft private and check with you before sharing it.

Share the word count and ask how much time they'll need. Agree on a date for feedback, including a time zone if it matters. If you're working towards a submission deadline, say so. Let them know how to reach you if they need more time, and whether partial feedback would help.

## Ask questions that leave room for surprise

Begin with an open invitation to share impressions. Then ask a few questions about moments in the reading experience, rather than questions that announce the answer you want. “Was the betrayal obvious?” can prompt a reader to look for a betrayal. A question about what they expected at a particular point leaves more room for their own response.

Andrew Noakes, founder of The Niche Reader, recommends combining broad and focused questions and placing story-specific concerns after the general response in <a href="https://janefriedman.com/what-to-ask-your-beta-readers/">What to Ask Your Beta Readers</a>. The template below has five questions and space for one about your current draft. Keep the ones that will help you revise.

If you're sending a sample, replace the question about the ending with one about what the reader expects next. Chapter or scene references make it easier to find the passages they're discussing.

## Copy this beta reader brief

Fill in the brackets and cut anything you don't need.

<a href="/beta-reader-brief.txt" download>Download the editable beta reader brief (.txt)</a>, or select and copy the text below. No signup is needed to use it.

{{beta-reader-brief}}

## Send it, then give them time to read

Before sending, check the filename, word count, content notes and deadline. Open the link or attachment yourself to make sure it works. Send the questions with the manuscript, check that everything arrived, then give your reader time to settle into the book.

When feedback arrives, thank them and read the whole response before planning changes. If a note is unclear, ask which passage prompted it. Keep the reader's reaction separate from a proposed fix: you may solve the underlying problem in a different way.

For the wider workflow, see <a href="/blog/the-beta-reading-process-what-to-expect">The Beta Reading Process: What to Expect</a>. You can also share our <a href="/blog/how-to-give-meaningful-beta-reader-feedback">guide to meaningful beta reader feedback</a> with someone who would like help framing their notes.

We're building Manuscript Match to help writers find readers who enjoy their genre. <a href="/">Join the waitlist</a> and we'll let you know when it's ready.
      `,
      briefTemplate: "BETA READER BRIEF\n\nThanks for considering my manuscript. Here are the details so you can decide whether you have time for it.\n\nTitle and author: [title / your name]\nGenre and intended readership: [genre / adult, YA or other audience]\nShort introduction: [one or two sentences, without explaining the ending]\nReading scope: [whole manuscript or chapters / word count of the file]\nDraft and filename: [version / date / filename]\nContent notes: [relevant subjects and level of detail; offer more information if wanted]\n\nThis round's focus: [one or two things you want to understand]\nFeedback requested: [e.g. your overall impressions and any scenes you found confusing]\nArrangement: [volunteer read / critique swap / paid service]\nFile and access: [agreed format / link or attachment / access instructions]\nPlease keep this draft and its contents private and ask before sharing it.\n\nFeedback due: [agreed date / time zone if relevant]\nReturn feedback by: [reply email / document / agreed method]\nIf you need more time or cannot finish: [how to reach me; whether partial feedback would help]\n\nPlease share your first impressions before answering these questions. Chapter or scene references are helpful; short answers are welcome.\n\n1. Which moment would you tell another reader about, and what made it memorable?\n2. Where did you pause because you needed information the story had not yet given you?\n3. Where did you feel most absorbed, and where did your attention drift?\n4. Which character choices felt convincing? Were there any you struggled to understand?\n5. After the final page, what felt resolved and what were you still wondering about?\n\nOptional question for this draft: [one neutral question, answered after reading]\nAnything else you would like me to know: [open response]\n\nThank you for taking the time to read and share your thoughts.\n",
      date: 'October 30, 2026',
      author: 'Manuscript Match Team',
      category: 'For Writers',
      readTime: '7 min read',
      icon: <EditIcon />,
      shareText: 'A practical beta reader brief: agree on the draft, reading scope, deadline and feedback questions before you send your manuscript.',
      hashtags: ['BetaReaders', 'WritingCommunity'],
      imageUrl: '/social-preview.png',
    },
    {
      id: '1',
      slug: 'the-power-of-beta-readers-how-feedback-shapes-great-stories',
      title: 'The Power of Beta Readers: How Feedback Shapes Great Stories',
      excerpt: 'Discover how beta readers can transform your manuscript from good to great. Learn what makes constructive feedback valuable and how to find readers who truly understand your genre.',
      content: `
# The Power of Beta Readers: How Feedback Shapes Great Stories

Every writer knows that feeling when you type "The End" on your manuscript. It's a mix of triumph, relief, and... uncertainty. Is it really done? Is it good enough? This is where beta readers become invaluable.

## What Makes Beta Readers Essential?

Beta readers offer fresh eyes on your work. After spending months or years with your manuscript, you become too close to see its flaws. You know what you meant to say, so you might miss:

- Plot holes that seem obvious to new readers
- Character inconsistencies that developed over time
- Pacing issues that drag or rush
- Unclear descriptions or confusing scenes
- Dialogue that doesn't ring true

## Finding the Right Beta Readers

Not all beta readers are created equal. The best beta readers for your manuscript:

1. **Read and enjoy your genre** - A romance reader won't give the same feedback on your thriller as someone who devours suspense novels.

2. **Understand storytelling** - They don't need to be writers, but they should be able to articulate what works and what doesn't.

3. **Can be honest but constructive** - "I loved it!" isn't helpful. Neither is "This is terrible." You need specific, actionable feedback.

4. **Respect deadlines** - Your timeline matters. Find readers who can commit to your schedule.

## What to Ask Beta Readers

Give your beta readers clear guidance:

- What stage is your manuscript in? (first draft, second draft, etc.)
- What kind of feedback are you looking for? (big picture, line-by-line, etc.)
- Are there specific areas of concern?
- What's your timeline?

Before you send your manuscript, use our <a href="/blog/beta-reader-brief-template">beta reader brief template</a> to agree on the scope, deadline and questions.

## Making the Most of Feedback

When you receive beta reader feedback:

1. **Don't respond immediately** - Let it sit for a day or two. Initial defensiveness is normal.

2. **Look for patterns** - If multiple readers mention the same issue, it's probably real.

3. **Consider the source** - Weight feedback based on the reader's experience with your genre.

4. **You're still the author** - Not all feedback needs to be implemented. Trust your instinct.

## The Manuscript Match Difference

We’re developing Manuscript Match to help writers find genre-matched beta readers. Join the waitlist for launch updates.

Ready to transform your manuscript with the power of beta reader feedback?
      `,
      date: 'November 8, 2025',
      author: 'Manuscript Match Team',
      category: 'For Writers',
      readTime: '5 min read',
      icon: <UsersIcon />,
      shareText: 'Just read this great article about finding beta readers who actually get your genre. So helpful for anyone trying to get real feedback on their manuscript!',
      hashtags: ['WritingCommunity', 'BetaReaders', 'AmWriting'],
      imageUrl: '/social-preview.png',
    },
    {
      id: '2',
      slug: 'building-your-author-platform-before-publication',
      title: 'Building Your Author Platform Before Publication',
      excerpt: 'Starting early can make all the difference. Explore strategies for building an engaged audience while you\'re still in the manuscript phase, and why literary agents care about your platform.',
      content: `
# Building Your Author Platform Before Publication

You've probably heard it before: "You need to build your author platform." But what does that actually mean, and why should you start before you're even published?

## What Is an Author Platform?

Simply put, your author platform is your ability to reach and engage with readers. It includes:

- Your social media following
- Your email list
- Your website or blog
- Your network in the writing community
- Any other way you can connect with potential readers

## Why Literary Agents Care

When agents consider your query, they're not just evaluating your manuscript. They're thinking about marketability. A strong author platform shows:

1. **You understand marketing** - Publishing is a business, and agents want partners who get that.

2. **You have an existing audience** - Even a small, engaged following is valuable.

3. **You're committed to your career** - Platform building shows you're serious about being an author.

## Starting Before Publication

Here's the good news: you don't need to be published to start building your platform. In fact, starting early gives you a huge advantage.

### Choose Your Channels Wisely

You don't need to be everywhere. Pick 1-2 platforms where your target readers hang out:

- **Twitter/X**: Great for connecting with the writing community
- **Instagram**: Perfect for visual storytelling and book aesthetics
- **TikTok**: BookTok is huge for certain genres
- **Newsletter**: The most valuable asset - you own your list

### What to Share

Before publication, share:

- Your writing journey
- Behind-the-scenes of your process
- Book recommendations in your genre
- Writing tips and lessons learned
- Your beta reading experiences

### Engage Authentically

Building a platform isn't about follower counts. It's about genuine connections:

- Respond to comments
- Support other writers
- Join conversations about books you love
- Share others' content, not just your own

## The Long Game

Platform building is a marathon, not a sprint. Start now, stay consistent, and focus on adding value to your community. When your book is ready, you'll have readers excited to support you.

We’re developing Manuscript Match as a place for writers and beta readers to connect. Join the waitlist for updates as the community takes shape.
      `,
      date: 'November 5, 2025',
      author: 'Manuscript Match Team',
      category: 'For Writers',
      readTime: '7 min read',
      icon: <EditIcon />,
      shareText: "This is really eye-opening about building an author platform before you're even published. Wish I'd known this sooner!",
      hashtags: ['AuthorPlatform', 'WritingCommunity', 'AmQuerying'],
      imageUrl: '/social-preview.png',
    },
    {
      id: '3',
      slug: 'what-literary-agents-really-look-for-in-query-letters',
      title: 'What Literary Agents Really Look For in Query Letters',
      excerpt: 'Get insider tips on crafting query letters that stand out. We break down the essential elements that catch an agent\'s eye and common mistakes to avoid.',
      content: `
# What Literary Agents Really Look For in Query Letters

Your query letter is often your only shot at catching an agent's attention. With hundreds of queries in their inbox, what makes them stop and request your manuscript?

## The Harsh Reality

Most agents spend 30 seconds or less on a query. If it doesn't grab them immediately, it's a pass. But here's the thing: a great query can overcome a lot.

## The Essential Elements

Every successful query letter includes:

### 1. A Compelling Hook

Your first paragraph needs to grab attention. Compare these:

**Weak**: "I am writing to query you about my fantasy novel."

**Strong**: "When a royal assassination forces street thief Mira to impersonate the dead princess, she has three days to learn to rule—or the kingdom falls to an ancient evil."

### 2. Clear Stakes

Agents need to understand:
- What does the protagonist want?
- What's stopping them?
- What happens if they fail?

### 3. Your Voice

Your query should reflect your writing style. If you've written a funny book, let your query be funny. Literary fiction? Show off your prose.

### 4. Professional Presentation

- Proper formatting
- No typos or grammatical errors
- Personalization showing you've researched the agent
- Correct word count and genre

## What Agents Don't Want to See

Avoid these common mistakes:

**Rhetorical questions**: "Have you ever wondered what it would be like to wake up with superpowers?"

**Comparing yourself to bestsellers**: "It's Harry Potter meets The Hunger Games!"

**Telling instead of showing**: "This is an exciting adventure that will keep readers on the edge of their seats."

**TMI about your writing process**: "I've been working on this for 10 years..."

## The Personalization Paragraph

This is where you show you've done your homework:

- Why this specific agent?
- What books they've represented that are similar?
- Did you meet them at a conference?

Generic "Dear Agent" queries get deleted. Show you care.

## Your Bio

Keep it relevant:

- Writing credentials (publications, awards, etc.)
- Platform (if substantial)
- Relevant expertise (wrote a medical thriller? Mention you're a doctor)

Don't mention:
- Your critique group thinks it's great
- Your family loves it
- Your high school writing award
- Why you wrote the book (unless it's relevant expertise)

## The Manuscript Match Advantage

Thoughtful beta-reader feedback can help you revise before querying. We’re developing tools to help writers find readers for their genre.

Remember: agents want to say yes. Give them a reason to.
      `,
      date: 'November 9, 2025',
      author: 'Manuscript Match Team',
      category: 'For Writers',
      readTime: '6 min read',
      icon: <BookIcon />,
      shareText: 'This breakdown of what agents actually look for in query letters is so good. Finally some practical advice instead of vague tips!',
      hashtags: ['QueryLetter', 'LiteraryAgents', 'AmQuerying'],
      imageUrl: '/social-preview.png',
    },
    {
      id: '4',
      slug: 'how-to-give-meaningful-beta-reader-feedback',
      title: 'How to Give Meaningful Beta Reader Feedback',
      excerpt: 'Being a beta reader is about more than just spotting typos. Learn how to provide thoughtful, constructive criticism that helps writers improve their craft while staying encouraging.',
      content: `
# How to Give Meaningful Beta Reader Feedback

Being asked to beta read is an honor—a writer is trusting you with their creative work. But how do you provide feedback that's actually helpful?

## Your Role as a Beta Reader

First, understand what you're NOT:
- Not a proofreader (that comes later)
- Not a developmental editor (though you might spot big issues)
- Not there to rewrite the book your way

You ARE:
- A representative of the target audience
- A fresh pair of eyes
- Someone who can identify what works and what doesn't

## What to Focus On

### Big Picture Issues

- **Plot**: Does the story make sense? Are there holes or inconsistencies?
- **Pacing**: Does it drag anywhere? Rush through important moments?
- **Characters**: Are they believable? Do you care about them?
- **Stakes**: Do you understand what's at risk?
- **Satisfaction**: Does the ending deliver on the story's promises?

### Scene-Level Feedback

- Which scenes really worked for you?
- Which scenes felt slow or unnecessary?
- Were there any confusing moments?
- Did any dialogue feel unnatural?

## How to Deliver Feedback

### Be Specific

**Not helpful**: "The middle was boring."

**Helpful**: "Chapters 12-15 felt slow to me. The protagonist is waiting for information, so there's not much happening. Maybe consider having her take more active steps?"

### Balance Criticism with Positivity

Always mention what's working. Writers need to know what to keep, not just what to fix.

### Ask Questions

Sometimes asking a question is more helpful than making a statement:

- "Was there a reason Character A did this?"
- "I'm confused about the timeline here—how much time has passed?"
- "What was the protagonist hoping to accomplish in this scene?"

### Suggest, Don't Demand

Remember, it's not your book. Offer ideas, but respect that the author makes the final call.

**Demanding**: "You need to cut this entire subplot."

**Suggesting**: "I found this subplot distracting from the main story. Have you considered whether it's necessary?"

## What About Typos and Grammar?

If you notice a pattern (consistent misspelling, overused word, etc.), mention it. But don't mark every typo—that's for the copyeditor stage.

## Organize Your Feedback

Make it easy for the author to use your feedback:

1. **Summary**: Overall impressions (2-3 paragraphs)
2. **Strengths**: What you loved
3. **Areas for Improvement**: Big picture issues
4. **Chapter Notes**: Specific comments organized by chapter
5. **Final Thoughts**: Encouragement and overall assessment

## The Golden Rule

Be the beta reader you'd want for your own work: honest but kind, specific but supportive, critical but encouraging.

## Join Manuscript Match

Our platform helps match beta readers with manuscripts in genres they love. Whether you're new to beta reading or an experienced reader, we make it easy to discover great manuscripts and provide valuable feedback to authors.

Ready to help shape amazing stories?
      `,
      date: 'November 7, 2025',
      author: 'Manuscript Match Team',
      category: 'For Beta Readers',
      readTime: '5 min read',
      icon: <BookIcon />,
      shareText: "If you beta read for friends, this is a must-read. Great tips on giving feedback that's actually helpful (not just 'I liked it!')",
      hashtags: ['BetaReader', 'WritingFeedback', 'BookCommunity'],
      imageUrl: '/social-preview.png',
    },
    {
      id: '5',
      slug: 'the-beta-reading-process-what-to-expect',
      title: 'The Beta Reading Process: What to Expect',
      excerpt: 'New to beta reading? This guide walks you through the entire process, from selecting manuscripts to submitting feedback, and how to build meaningful connections with authors.',
      content: `
# The Beta Reading Process: What to Expect

Interested in becoming a beta reader but not sure where to start? Here's everything you need to know about the beta reading process.

## What Is Beta Reading?

Beta reading happens after the author has self-edited their manuscript but before professional editing. You're reading to provide feedback on the story itself—plot, characters, pacing, and overall reader experience.

## The Process Step by Step

### 1. Finding Manuscripts

The workflow we’re planning for Manuscript Match would help readers:
- Browse available manuscripts by genre
- Read sample chapters
- Review the author's expectations
- See the manuscript's word count and deadline

### 2. Applying to Read

When you find a manuscript that interests you:
- Send a brief message explaining why you're a good fit
- Mention your experience with the genre
- Confirm you can meet the deadline
- Ask any questions you have

### 3. Getting Accepted

If the author chooses you:
- You'll receive the full manuscript
- Review the author's specific questions
- Clarify the feedback format they prefer
- Confirm the deadline

### 4. Reading and Note-Taking

As you read:
- **Don't overthink it** - Your genuine reactions are valuable
- **Take notes as you go** - Don't wait until the end
- **Mark confusing parts** - If you had to reread something, note it
- **Track your emotions** - When were you excited? Bored? Confused?

### 5. Providing Feedback

Depending on the author's preference, this might include:
- A summary document with overall impressions
- Chapter-by-chapter notes
- Answers to specific questions
- Margin comments in the manuscript

### 6. Discussion

Many authors want to discuss your feedback:
- Be open to questions
- Explain your reasoning
- Remember you're collaborating, not dictating
- Stay encouraging

## Time Commitment

Beta reading typically takes:
- **Reading**: 1-2 weeks for a novel (depending on length)
- **Feedback prep**: 2-4 hours for summary and notes
- **Discussion**: 30-60 minutes (optional)

## What You Get Out of It

Beyond the joy of reading unpublished books, beta reading offers:

1. **Free books** - Read before anyone else
2. **Acknowledgment** - Many authors thank beta readers in their published books
3. **Networking** - Build relationships with authors
4. **Skill building** - Improve your understanding of craft
5. **Community** - Join a supportive literary community

## Beta Reading Etiquette

### Do:
- Communicate clearly and promptly
- Meet your deadlines (or give advance notice if you can't)
- Be honest but kind
- Keep the manuscript confidential
- Celebrate the author's strengths

### Don't:
- Share the manuscript with anyone
- Ghost the author if you can't finish
- Be cruel or dismissive
- Rewrite the book your way
- Compare it unfavorably to published books

## Building Relationships

The best beta reading experiences often lead to ongoing relationships:
- You might beta read for the same author again
- Authors might recommend you to other writers
- You'll build a network in the writing community

## Ready to Start?

Manuscript Match is in development. We’re planning a place to discover manuscripts that fit your reading interests. Join the waitlist for launch updates.

Join our community of passionate readers helping to shape the next generation of great books.
      `,
      date: 'November 5, 2025',
      author: 'Manuscript Match Team',
      category: 'For Beta Readers',
      readTime: '4 min read',
      icon: <UsersIcon />,
      shareText: "Always wanted to try beta reading but wasn't sure where to start? This walks through the whole process in a really clear way.",
      hashtags: ['BetaReading', 'BookLovers', 'ReadingCommunity'],
      imageUrl: '/social-preview.png',
    },
    {
      id: '6',
      slug: 'common-manuscript-mistakes-and-how-to-fix-them',
      title: 'Common Manuscript Mistakes and How to Fix Them',
      excerpt: 'Learn about the most frequent issues found in unpublished manuscripts and practical strategies to address them before submitting to agents or beta readers.',
      content: `
# Common Manuscript Mistakes and How to Fix Them

Recurring draft issues can be easier to spot with a fresh reader. Here are examples to check in your own manuscript.

## 1. Starting Too Early

**The Problem**: Many manuscripts start with the protagonist waking up, describing their normal day, or providing extensive backstory before anything happens.

**The Fix**: Start with action, conflict, or change. You can weave in backstory later. Consider cutting the first chapter entirely—often the real story starts in chapter two.

**Ask yourself**: What's the inciting incident? Can you start closer to it?

## 2. Telling Instead of Showing

**The Problem**:
"Sarah was angry" instead of showing her slamming doors and snapping at people.

**The Fix**:
- Use action and dialogue to convey emotion
- Let readers draw conclusions
- Trust your readers to understand subtext

**Exercise**: Go through your manuscript and highlight every instance of "felt," "was," "thought." Can you show these instead?

## 3. Info Dumping

**The Problem**: Paragraphs of worldbuilding, backstory, or explanation that stop the story dead.

**The Fix**:
- Spread information throughout the story
- Reveal things as they become relevant
- Let characters discover information alongside readers

**Rule of thumb**: If you can delete a paragraph without affecting the current scene, it's probably an info dump.

## 4. Weak Stakes

**The Problem**: Readers don't understand or care what's at risk.

**The Fix**:
- Make sure we care about the character first
- Be specific about what they'll lose
- Raise the stakes as the story progresses
- Connect external stakes to internal growth

**Test**: Can you clearly state what happens if your protagonist fails?

## 5. Passive Protagonist

**The Problem**: Things happen TO the protagonist instead of the protagonist making things happen.

**The Fix**:
- Give your protagonist agency
- Let them make difficult choices
- Make them face consequences
- Ensure they're active in their own story

**Warning sign**: Is your protagonist mostly reacting, waiting, or being rescued?

## 6. Unrealistic Dialogue

**The Problem**:
- Everyone sounds the same
- Too formal or stilted
- On-the-nose (characters say exactly what they mean)
- "As you know, Bob" exposition

**The Fix**:
- Read dialogue aloud
- Give each character distinct speech patterns
- Use subtext—people rarely say exactly what they mean
- Cut greetings and small talk
- Remember that people interrupt, trail off, and speak imperfectly

## 7. Pacing Issues

**The Problem**:
- Middle drags
- Rushing through important moments
- Too much happening too fast

**The Fix**:
- Vary scene length and intensity
- Slow down for important moments
- Speed up for transitions
- Create a scene-by-scene outline to see pacing visually
- Cut scenes that don't advance plot or character

## 8. Inconsistent POV

**The Problem**:
- Head-hopping within scenes
- POV character knowing things they shouldn't
- Telling other characters' thoughts in close POV

**The Fix**:
- Choose your POV for each scene and stick with it
- If you switch, make it clear (scene break, chapter break)
- Stay in your POV character's head and knowledge

**Check**: Read each scene. Could you film it from one camera position?

## 9. Weak Endings

**The Problem**:
- Deus ex machina (convenient solution appears)
- All tied up too neatly
- Or nothing resolved at all
- Doesn't deliver on the story's promises

**The Fix**:
- Ensure the protagonist solves their own problem
- Use skills/knowledge established earlier
- Resolve the main conflict
- Leave some threads for reader imagination
- Make sure the ending is earned

## 10. Not Enough Revision

**The Problem**: Submitting first drafts or stopping after one revision.

**The Fix**:
- First draft is for you; revision is for readers
- Do multiple passes, each with a different focus
- Get beta reader feedback
- Let it sit between revisions
- Be willing to make major changes

## The Beta Reading Solution

The best way to catch these issues? Fresh eyes. Beta readers will spot problems you can't see because you're too close to your work.

We’re building Manuscript Match to help writers find beta readers for thoughtful, constructive feedback before querying agents. Join the waitlist for updates.

Ready to level up your manuscript?
      `,
      date: 'November 10, 2025',
      author: 'Manuscript Match Team',
      category: 'For Writers',
      readTime: '8 min read',
      icon: <EditIcon />,
      shareText: "Oof, I'm definitely guilty of a few of these manuscript mistakes. Good to know what to fix before sending to beta readers!",
      hashtags: ['WritingTips', 'ManuscriptEditing', 'AmEditing'],
      imageUrl: '/social-preview.png',
    },
  ];

  const post = blogPosts.find(p => p.slug === slug);

  // Update meta tags for SEO when post changes
  React.useEffect(() => {
    if (post) {
      updateMetaTags(post);
    }
  }, [post]);

  if (!post) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        color: theme.colors.textPrimary,
        fontFamily: theme.fonts.primary,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
      }}>
        <h1>Post Not Found</h1>
        <a href="/blog" style={{ color: theme.colors.primary }}>Back to Blog</a>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      color: theme.colors.textPrimary,
      fontFamily: theme.fonts.primary,
    }}>
      {/* Responsive CSS */}
      <style>{`
        @media (max-width: 768px) {
          .post-header {
            padding: 16px 20px !important;
          }
          .post-logo {
            width: 32px !important;
            height: 32px !important;
          }
          .post-logo-text {
            font-size: 18px !important;
          }
          .post-hero {
            padding: 48px 24px !important;
          }
          .post-title {
            font-size: 32px !important;
            margin-bottom: 16px !important;
          }
          .post-meta {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 12px !important;
          }
          .post-content {
            padding: 48px 24px !important;
          }
          .post-content-wrapper {
            flex-direction: column !important;
          }
          .post-sidebar {
            display: none !important;
          }
          .post-article h1 {
            font-size: 28px !important;
          }
          .post-article h2 {
            font-size: 22px !important;
          }
          .post-article h3 {
            font-size: 18px !important;
          }
        }
      `}</style>

      {/* Header */}
      <header className="post-header" style={{
        backgroundColor: theme.colors.white,
        borderBottom: `1px solid ${theme.colors.border}`,
        padding: '20px 40px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <a
            href="/"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              textDecoration: 'none',
            }}
          >
            <img
              className="post-logo"
              src="/ManuscriptMatchLogo.svg"
              alt="Manuscript Match Logo"
              style={{
                width: '40px',
                height: '40px',
              }}
            />
            <div className="post-logo-text" style={{
              fontSize: '24px',
              fontWeight: '600',
              color: theme.colors.primary,
              letterSpacing: '-0.01em',
            }}>
              Manuscript Match
            </div>
          </a>
          <a
            href="/blog"
            style={{
              color: theme.colors.textSecondary,
              textDecoration: 'none',
              fontSize: '15px',
              fontWeight: '500',
              transition: 'color 0.2s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = theme.colors.primary;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.color = theme.colors.textSecondary;
            }}
          >
            ← Back to Blog
          </a>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="post-hero" style={{
          backgroundColor: theme.colors.backgroundAlt,
          padding: '80px 40px',
          borderBottom: `1px solid ${theme.colors.border}`,
        }}>
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
          }}>
            <div style={{
              display: 'inline-block',
              backgroundColor: theme.colors.primaryLight,
              color: theme.colors.primaryDark,
              padding: '6px 14px',
              borderRadius: theme.borderRadius.pill,
              fontSize: '13px',
              fontWeight: '600',
              marginBottom: '24px',
              letterSpacing: '0.02em',
            }}>
              {post.category}
            </div>
            <h1 className="post-title" itemProp="headline" style={{
              fontSize: '48px',
              fontWeight: '600',
              marginBottom: '24px',
              color: theme.colors.textPrimary,
              letterSpacing: '-0.02em',
              lineHeight: '1.1',
            }}>
              {post.title}
            </h1>
            <div className="post-meta" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '24px',
              color: theme.colors.textSecondary,
              fontSize: '15px',
            }}>
              <span itemProp="author">{post.author}</span>
              <span>•</span>
              <time itemProp="datePublished" dateTime={new Date(post.date).toISOString()}>{post.date}</time>
              <span>•</span>
              <span>{post.readTime}</span>
            </div>

            {/* Share Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '32px',
              flexWrap: 'wrap',
            }}>
              <a
                href={getTwitterShareUrl(post)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  backgroundColor: theme.colors.white,
                  color: theme.colors.textSecondary,
                  border: `2px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.medium,
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.primary;
                  e.currentTarget.style.color = theme.colors.primary;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.border;
                  e.currentTarget.style.color = theme.colors.textSecondary;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <TwitterIcon width={16} height={16} />
                Share on Twitter
              </a>

              <a
                href={getBlueskyShareUrl(post)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  backgroundColor: theme.colors.white,
                  color: theme.colors.textSecondary,
                  border: `2px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.medium,
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.primary;
                  e.currentTarget.style.color = theme.colors.primary;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.border;
                  e.currentTarget.style.color = theme.colors.textSecondary;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <BlueskyIcon width={16} height={16} />
                Share on Bluesky
              </a>
            </div>
          </div>
        </section>

        {/* Article Content with Sidebar */}
        <div className="post-content" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '80px 40px',
        }}>
          <div className="post-content-wrapper" style={{
            display: 'flex',
            gap: '60px',
            alignItems: 'flex-start',
          }}>
            {/* Main Article */}
            <article itemScope itemType="https://schema.org/BlogPosting" style={{
              flex: '1',
              minWidth: 0,
            }}>
              {/* Hidden meta for SEO */}
              <meta itemProp="description" content={post.excerpt} />
              <link itemProp="url" href={canonicalUrl} />

              <div
                className="post-article"
                itemProp="articleBody"
                style={{
                  fontSize: '18px',
                  lineHeight: '1.8',
                  color: theme.colors.textPrimary,
                }}
                dangerouslySetInnerHTML={{
                  __html: post.content
                    .split('\n')
                    .filter(line => line.trim() !== `# ${post.title}`)
                    .map(line => {
                      if (line.trim() === '{{beta-reader-brief}}' && post.briefTemplate) {
                        const safeBrief = post.briefTemplate.replace(/[&<>]/g, character =>
                          character === '&' ? '&amp;' : character === '<' ? '&lt;' : '&gt;');
                        return `<pre aria-label="Copyable beta reader brief" tabindex="0" style="white-space: pre-wrap; overflow-wrap: anywhere; padding: 24px; border: 1px solid ${theme.colors.border}; border-radius: 8px; background: ${theme.colors.backgroundAlt}; font: inherit; font-size: 16px; line-height: 1.7;">${safeBrief}</pre>`;
                      }
                      // Helper function to process inline markdown
                      const processInlineMarkdown = (text: string) => {
                        // Convert bold text (**text**)
                        text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
                        // Convert italic text (*text* or _text_)
                        text = text.replace(/\*(.+?)\*/g, '<em>$1</em>');
                        text = text.replace(/_(.+?)_/g, '<em>$1</em>');
                        return text;
                      };

                      // Convert markdown-style headings
                      if (line.startsWith('# ')) {
                        return `<h1 style="font-size: 36px; font-weight: 600; margin: 48px 0 24px 0; color: ${theme.colors.textPrimary}; letter-spacing: -0.01em;">${processInlineMarkdown(line.substring(2))}</h1>`;
                      }
                      if (line.startsWith('## ')) {
                        return `<h2 style="font-size: 28px; font-weight: 600; margin: 40px 0 20px 0; color: ${theme.colors.textPrimary}; letter-spacing: -0.01em;">${processInlineMarkdown(line.substring(3))}</h2>`;
                      }
                      if (line.startsWith('### ')) {
                        return `<h3 style="font-size: 22px; font-weight: 600; margin: 32px 0 16px 0; color: ${theme.colors.textPrimary};">${processInlineMarkdown(line.substring(4))}</h3>`;
                      }
                      // Convert bold text on its own line
                      if (line.startsWith('**') && line.endsWith('**')) {
                        return `<p style="margin: 16px 0; font-weight: 600;">${processInlineMarkdown(line.substring(2, line.length - 2))}</p>`;
                      }
                      // Convert list items
                      if (line.match(/^[-*]\s/)) {
                        return `<li style="margin: 8px 0; margin-left: 24px;">${processInlineMarkdown(line.substring(2))}</li>`;
                      }
                      if (line.match(/^\d+\.\s/)) {
                        return `<li style="margin: 8px 0; margin-left: 24px;">${processInlineMarkdown(line.substring(line.indexOf('.') + 2))}</li>`;
                      }
                      // Regular paragraphs
                      if (line.trim()) {
                        return `<p style="margin: 20px 0; line-height: 1.8;">${processInlineMarkdown(line)}</p>`;
                      }
                      return '';
                    })
                    .join('')
                }}
              />

              {/* Share Buttons - Bottom */}
              <div style={{
                marginTop: '60px',
                paddingTop: '40px',
                borderTop: `2px solid ${theme.colors.border}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '20px',
              }}>
                <p style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: theme.colors.textPrimary,
                  margin: 0,
                }}>
                  Found this helpful? Share it!
                </p>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}>
                  <a
                    href={getTwitterShareUrl(post)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      backgroundColor: theme.colors.white,
                      color: theme.colors.textSecondary,
                      border: `2px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.medium,
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = theme.colors.primary;
                      e.currentTarget.style.color = theme.colors.primary;
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor = theme.colors.border;
                      e.currentTarget.style.color = theme.colors.textSecondary;
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <TwitterIcon width={16} height={16} />
                    Share on Twitter
                  </a>

                  <a
                    href={getBlueskyShareUrl(post)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      backgroundColor: theme.colors.white,
                      color: theme.colors.textSecondary,
                      border: `2px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.medium,
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = theme.colors.primary;
                      e.currentTarget.style.color = theme.colors.primary;
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor = theme.colors.border;
                      e.currentTarget.style.color = theme.colors.textSecondary;
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <BlueskyIcon width={16} height={16} />
                    Share on Bluesky
                  </a>
                </div>
              </div>

              {/* CTA */}
              <div style={{
                marginTop: '80px',
                backgroundColor: theme.colors.backgroundAlt,
                borderRadius: theme.borderRadius.xlarge,
                padding: '40px',
                textAlign: 'center',
                border: `1px solid ${theme.colors.border}`,
              }}>
                <h3 style={{
                  fontSize: '28px',
                  fontWeight: '600',
                  marginBottom: '16px',
                  color: theme.colors.textPrimary,
                  letterSpacing: '-0.01em',
                }}>
                  Join the Waitlist
                </h3>
                <p style={{
                  fontSize: '16px',
                  color: theme.colors.textSecondary,
                  marginBottom: '24px',
                  lineHeight: '1.6',
                }}>
                  Be among the first to experience Manuscript Match when we launch.
                </p>
                <button
                  onClick={() => setIsModalOpen(true)}
                  style={{
                    backgroundColor: theme.colors.primary,
                    color: 'white',
                    border: 'none',
                    padding: '12px 32px',
                    borderRadius: theme.borderRadius.medium,
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.primaryDark;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.primary;
                  }}
                >
                  Get Early Access
                </button>
              </div>
            </article>

            {/* Sidebar - Suggested Articles */}
            <aside className="post-sidebar" style={{
              width: '320px',
              flexShrink: 0,
            }}>
              <div style={{
                position: 'sticky',
                top: '100px',
              }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  marginBottom: '24px',
                  color: theme.colors.textPrimary,
                }}>
                  Suggested Reading
                </h3>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                }}>
                  {blogPosts
                    .filter(p => p.slug !== post.slug)
                    .slice(0, 3)
                    .map((suggestedPost) => (
                      <a
                        key={suggestedPost.id}
                        href={`/blog/${suggestedPost.slug}`}
                        style={{
                          textDecoration: 'none',
                          display: 'block',
                          padding: '16px',
                          backgroundColor: theme.colors.white,
                          border: `1px solid ${theme.colors.border}`,
                          borderRadius: theme.borderRadius.large,
                          transition: 'all 0.2s',
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px',
                        }}>
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '40px',
                            height: '40px',
                            backgroundColor: theme.colors.primaryLight,
                            borderRadius: theme.borderRadius.medium,
                            color: theme.colors.primary,
                            flexShrink: 0,
                          }}>
                            {suggestedPost.icon}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: '11px',
                              color: theme.colors.textTertiary,
                              marginBottom: '6px',
                              textTransform: 'uppercase',
                              fontWeight: '600',
                              letterSpacing: '0.05em',
                            }}>
                              {suggestedPost.category}
                            </div>
                            <h4 style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              color: theme.colors.textPrimary,
                              marginBottom: '6px',
                              lineHeight: '1.4',
                            }}>
                              {suggestedPost.title}
                            </h4>
                            <div style={{
                              fontSize: '12px',
                              color: theme.colors.textTertiary,
                            }}>
                              {suggestedPost.readTime}
                            </div>
                          </div>
                        </div>
                      </a>
                    ))}
                </div>

                {/* Newsletter CTA in Sidebar */}
                <div style={{
                  marginTop: '32px',
                  backgroundColor: theme.colors.primaryLight,
                  borderRadius: theme.borderRadius.large,
                  padding: '20px',
                  border: `1px solid ${theme.colors.primary}`,
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: theme.colors.primaryDark,
                  }}>
                    Join Our Waitlist
                  </h4>
                  <p style={{
                    fontSize: '13px',
                    color: theme.colors.textSecondary,
                    marginBottom: '16px',
                    lineHeight: '1.5',
                  }}>
                    Be the first to know when we launch.
                  </p>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    style={{
                      width: '100%',
                      backgroundColor: theme.colors.primary,
                      color: 'white',
                      border: 'none',
                      padding: '10px 16px',
                      borderRadius: theme.borderRadius.medium,
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.primaryDark;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.primary;
                    }}
                  >
                    Get Early Access
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
      <WaitlistModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};
